/**
 * Dou Dizhu combinations (牌型): reading a set of cards, comparing, choosing a reading.
 * Every clarification of the spec (section 1 «Комбинации») has a test in patterns.test.ts:
 * wings never use the main ranks and never both jokers; pair wings are distinct ranks; 4 + 2 is not a bomb;
 * chains run within 3…A.
 */
import { BIG_JOKER, RANK_A, RANK_BJ, SMALL_JOKER, ddzRank, ddzSort, rankCounts } from './cards';
import type { DdzPattern, DdzPatternKey, DdzPatternType } from './types';

/** Reading priority on a free lead when one set of cards reads several ways (then longer, then higher). */
export const DDZ_LEAD_PRIORITY: readonly DdzPatternType[] = [
  'rocket',
  'bomb',
  'plane',
  'plane2',
  'plane1',
  'pairs',
  'straight',
  'four22',
  'four2',
  'trio2',
  'trio1',
  'trio',
  'pair',
  'single',
];

export function ddzPattern(
  type: DdzPatternType,
  rank: number,
  len: number,
  cards: readonly number[],
): DdzPattern {
  return { type, rank, len, cards: ddzSort(cards) };
}

/** ranks start … start + len − 1 all hold exactly `n` cards, within 3…A */
function run(counts: readonly number[], start: number, len: number, n: number): boolean {
  if (start + len - 1 > RANK_A) return false;
  for (let r = start; r < start + len; r++) if (counts[r] !== n) return false;
  return true;
}

const hasRocket = (cards: readonly number[]): boolean =>
  cards.includes(SMALL_JOKER) && cards.includes(BIG_JOKER);

/** Every reading of `cards` (empty = not a combination). Duplicate ids are not a combination; bad ids throw. */
export function ddzClassify(cards: readonly number[]): DdzPattern[] {
  const n = cards.length;
  if (n === 0 || new Set(cards).size !== n) return [];
  const c = rankCounts(cards);
  const present: number[] = [];
  for (let r = 0; r < c.length; r++) if ((c[r] as number) > 0) present.push(r);
  const lo = present[0] as number;
  const withCount = (m: number): number[] => present.filter((r) => c[r] === m);
  const p = (type: DdzPatternType, rank: number, len = 1): DdzPattern => ddzPattern(type, rank, len, cards);

  if (n === 2 && hasRocket(cards)) return [p('rocket', RANK_BJ)];
  if (present.length === 1)
    return [p((['single', 'pair', 'trio', 'bomb'] as const)[n - 1] as DdzPatternType, lo)];

  const out: DdzPattern[] = [];
  const trios = withCount(3);
  if (n === 4 && trios.length === 1) out.push(p('trio1', trios[0] as number));
  if (n === 5 && trios.length === 1 && withCount(2).length === 1) out.push(p('trio2', trios[0] as number));
  for (const q of withCount(4)) {
    if (n === 6 && !hasRocket(cards.filter((x) => ddzRank(x) !== q))) out.push(p('four2', q));
    if (n === 8 && present.length === 3 && present.every((r) => r === q || c[r] === 2))
      out.push(p('four22', q));
  }
  if (n >= 5 && present.length === n && run(c, lo, n, 1)) out.push(p('straight', lo, n));
  if (n >= 6 && n % 2 === 0 && present.length === n / 2 && run(c, lo, n / 2, 2))
    out.push(p('pairs', lo, n / 2));
  if (n >= 6 && n % 3 === 0 && present.length === n / 3 && run(c, lo, n / 3, 3))
    out.push(p('plane', lo, n / 3));
  if (n >= 8 && n % 4 === 0) {
    const k = n / 4;
    for (let s = 0; s + k - 1 <= RANK_A; s++) {
      if (!run(c, s, k, 3)) continue;
      const wings = cards.filter((x) => ddzRank(x) < s || ddzRank(x) >= s + k);
      if (!hasRocket(wings)) out.push(p('plane1', s, k));
    }
  }
  if (n >= 10 && n % 5 === 0) {
    const k = n / 5;
    for (let s = 0; s + k - 1 <= RANK_A; s++) {
      if (!run(c, s, k, 3)) continue;
      const others = present.filter((r) => r < s || r >= s + k);
      if (others.length === k && others.every((r) => c[r] === 2)) out.push(p('plane2', s, k));
    }
  }
  return out;
}

/** The rocket beats everything; a bomb beats all but a higher bomb and the rocket; the rest — same type, same length, higher rank. */
export function ddzBeats(p: DdzPatternKey, prev: DdzPatternKey): boolean {
  if (prev.type === 'rocket') return false;
  if (p.type === 'rocket') return true;
  if (p.type === 'bomb') return prev.type !== 'bomb' || p.rank > prev.rank;
  if (prev.type === 'bomb') return false;
  return p.type === prev.type && p.len === prev.len && p.rank > prev.rank;
}

export const sameKey = (a: DdzPatternKey, b: DdzPatternKey): boolean =>
  a.type === b.type && a.rank === b.rank && a.len === b.len;

const PRIORITY = new Map<DdzPatternType, number>(DDZ_LEAD_PRIORITY.map((t, i) => [t, i]));
const byLead = (a: DdzPattern, b: DdzPattern): number =>
  (PRIORITY.get(a.type) as number) - (PRIORITY.get(b.type) as number) || b.len - a.len || b.rank - a.rank;

/**
 * The reading used for a play: `as` (if given) must be one of the readings; following — only readings that beat `prev`
 * (a set reading as a bomb or the rocket has no other reading, so all candidates share the type); then by DDZ_LEAD_PRIORITY,
 * longer, higher. null = the cards cannot be played here.
 */
export function ddzRead(
  cards: readonly number[],
  prev: DdzPatternKey | null,
  as?: DdzPatternKey,
): DdzPattern | null {
  let options = ddzClassify(cards);
  if (as) options = options.filter((o) => sameKey(o, as));
  if (prev) options = options.filter((o) => ddzBeats(o, prev));
  return options.sort(byLead)[0] ?? null;
}
